import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { formatLocalDate, parseServerDateTime, toEpochMs } from './timezone';
import PredictedNextChip from './PredictedNextChip';

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  completed: { bg: '#14532d', text: '#4ade80', dot: '#22c55e' },
  failed:    { bg: '#450a0a', text: '#f87171', dot: '#ef4444' },
  running:   { bg: '#1e3a5f', text: '#60a5fa', dot: '#3b82f6' },
  queued:    { bg: '#292524', text: '#a8a29e', dot: '#78716c' },
  paused:    { bg: '#3b2700', text: '#fbbf24', dot: '#f59e0b' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.queued;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: s.bg, color: s.text,
      borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0,
        animation: status === 'running' ? 'pulse 1.5s infinite' : 'none' }} />
      {status}
    </span>
  );
}

// ─── Duration helpers ─────────────────────────────────────────────────────────

function fmtDuration(start, end) {
  if (!start) return '—';
  const startMs = toEpochMs(start);
  if (startMs == null) return '—';
  const endMs = end ? toEpochMs(end) : Date.now();
  if (endMs == null) return '—';
  const sec = Math.round((endMs - startMs) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = parseServerDateTime(iso);
  if (!d) return '';
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 60000) return 'just now';
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
  return formatLocalDate(d);
}

// ─── AI Error Explainer ───────────────────────────────────────────────────────

function AiErrorExplainer({ runId }) {
  const [open, setOpen] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [credId, setCredId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchCreds = async () => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API_BASE_URL}/credentials`, { headers });
      const ai = (res.data || []).filter(c => ['openai', 'gemini', 'openai_compatible'].includes((c.service_type || '').toLowerCase()));
      setCredentials(ai);
      if (ai.length > 0) setCredId(ai[0].id);
    } catch (_) {}
  };

  const handleOpen = async () => {
    setOpen(true);
    setResult(null);
    setError('');
    if (credentials.length === 0) await fetchCreds();
  };

  const handleExplain = async () => {
    if (!credId) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_BASE_URL}/ai/explain-error`, { run_id: runId, credential_id: credId }, { headers });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Explanation failed');
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <button onClick={handleOpen} style={{ background: 'none', border: '1px solid rgba(99,102,241,0.4)', borderRadius: 5, color: '#a5b4fc', fontSize: 11, padding: '3px 8px', cursor: 'pointer', marginTop: 4 }}>
        🤖 Explain error
      </button>
    );
  }

  return (
    <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 12px', marginTop: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#a5b4fc' }}>🤖 AI Error Explainer</span>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 16 }}>×</button>
      </div>
      {credentials.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>No OpenAI/Gemini credentials found. Add one in Settings.</div>
      ) : !result ? (
        <>
          <select value={credId} onChange={e => setCredId(e.target.value)} style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12, marginBottom: 8 }}>
            {credentials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {error && <div style={{ color: '#f87171', fontSize: 11, marginBottom: 6 }}>{error}</div>}
          <button onClick={handleExplain} disabled={loading} style={{ width: '100%', padding: '8px', borderRadius: 6, border: 'none', background: '#6366f1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Analyzing…' : 'Explain this error'}
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.5, marginBottom: 10 }}>{result.explanation}</div>
          {result.suggestions && result.suggestions.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suggestions</div>
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                {result.suggestions.map((s, i) => <li key={i} style={{ fontSize: 12, color: 'var(--text-primary)', marginBottom: 4, lineHeight: 1.4 }}>{s}</li>)}
              </ol>
            </>
          )}
          <button onClick={() => setResult(null)} style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer' }}>Try again</button>
        </>
      )}
    </div>
  );
}

// ─── Run card ─────────────────────────────────────────────────────────────────

function RunCard({ run, workflowName, workflowId, onApprove, onReject }) {
  const isPaused = run.status === 'paused';
  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: `1px solid ${isPaused ? '#f59e0b' : 'var(--border-color)'}`,
      borderRadius: 12,
      padding: '14px 16px',
      marginBottom: 10,
      boxShadow: isPaused ? '0 0 0 1px #f59e0b33' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {workflowName || 'Workflow'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
            {fmtTime(run.start_time)} · {fmtDuration(run.start_time, run.end_time)}
            {run.trigger_type && <span style={{ marginLeft: 6, opacity: 0.7 }}>· {run.trigger_type}</span>}
          </div>
        </div>
        <StatusBadge status={run.status} />
      </div>

      {run.status === 'running' && workflowId && (
        <div style={{ marginBottom: 8 }}>
          <PredictedNextChip
            sourceType="workflow_run"
            sourceDefId={workflowId}
            runId={run.id}
            compact
          />
        </div>
      )}

      {run.error && (
        <div style={{ background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#fca5a5', marginBottom: 4, wordBreak: 'break-word' }}>
          {run.error.slice(0, 200)}{run.error.length > 200 ? '…' : ''}
        </div>
      )}
      {run.status === 'failed' && <AiErrorExplainer runId={run.id} />}

      {isPaused && (
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button
            onClick={() => onApprove(run)}
            style={{ flex: 1, padding: '12px 0', background: '#166534', color: '#4ade80', border: '1px solid #166534', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', minHeight: 48 }}
          >
            ✓ Approve
          </button>
          <button
            onClick={() => onReject(run)}
            style={{ flex: 1, padding: '12px 0', background: '#450a0a', color: '#f87171', border: '1px solid #450a0a', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', minHeight: 48 }}
          >
            ✗ Reject
          </button>
        </div>
      )}

      {run.sla_breached && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 4 }}>
          ⚠ SLA breached {run.sla_breach_at ? `at ${fmtTime(run.sla_breach_at)}` : ''}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MobileRunMonitor({ getAuthHeaders, currentWorkspace, onBack }) {
  const [runs, setRuns]             = useState([]);   // [{run, workflowName, workflowId}]
  const [loading, setLoading]       = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]           = useState('');
  const [filter, setFilter]         = useState('all'); // all | running | failed | paused
  const [interactions, setInteractions] = useState([]);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  const fetchRuns = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      // 1. Get all accessible workflows
      const wfRes = await axios.get(`${API_BASE_URL}/workflows/`, { headers: getAuthHeaders() });
      const workflows = (wfRes.data || []).filter(wf =>
        !currentWorkspace || wf.workspace_id === currentWorkspace?.id
      );

      // 2. Get recent runs for each workflow (up to 5 per workflow, last 24h)
      const runPromises = workflows.slice(0, 20).map(wf =>
        axios.get(`${API_BASE_URL}/workflows/${wf.id}/runs?limit=5`, { headers: getAuthHeaders() })
          .then(r => (r.data?.runs || r.data || []).map(run => ({ run, workflowName: wf.name, workflowId: wf.id })))
          .catch(() => [])
      );

      const results = await Promise.all(runPromises);
      const all = results.flat().sort((a, b) => (toEpochMs(b.run.start_time) || 0) - (toEpochMs(a.run.start_time) || 0));
      setRuns(all);

      // 3. Fetch pending human interactions
      const intRes = await axios.get(`${API_BASE_URL}/human-interactions?status=pending`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }));
      setInteractions(intRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load runs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, currentWorkspace]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Pull-to-refresh
  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    const container = containerRef.current;
    if (delta > 60 && container && container.scrollTop === 0) {
      fetchRuns(true);
    }
  };

  async function handleApprove(run) {
    const interaction = interactions.find(i => i.run_id === run.id && i.status === 'pending');
    if (!interaction) { alert('No pending approval found for this run.'); return; }
    try {
      await axios.post(
        `${API_BASE_URL}/human-interactions/${interaction.id}/respond`,
        { approved: true, response: {} },
        { headers: getAuthHeaders() }
      );
      fetchRuns(true);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to approve');
    }
  }

  async function handleReject(run) {
    const interaction = interactions.find(i => i.run_id === run.id && i.status === 'pending');
    if (!interaction) { alert('No pending approval found for this run.'); return; }
    try {
      await axios.post(
        `${API_BASE_URL}/human-interactions/${interaction.id}/respond`,
        { approved: false, response: { reason: 'Rejected from mobile monitor' } },
        { headers: getAuthHeaders() }
      );
      fetchRuns(true);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to reject');
    }
  }

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'running', label: '▶ Running' },
    { key: 'paused', label: '⏸ Pending' },
    { key: 'failed', label: '✗ Failed' },
  ];

  const filtered = filter === 'all' ? runs : runs.filter(({ run }) => run.status === filter);
  const pendingCount = runs.filter(({ run }) => run.status === 'paused').length;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      background: 'var(--bg-primary)', color: 'var(--text-primary)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      maxWidth: 600, margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 16px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 20, padding: '4px 6px', lineHeight: 1 }}
                aria-label="Back"
              >
                ←
              </button>
            )}
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>Run Monitor</div>
              {pendingCount > 0 && (
                <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 2 }}>
                  {pendingCount} run{pendingCount > 1 ? 's' : ''} waiting for approval
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => fetchRuns(true)}
            disabled={refreshing}
            style={{ background: 'none', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, opacity: refreshing ? 0.5 : 1 }}
          >
            {refreshing ? '…' : '↻'}
          </button>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                flexShrink: 0, padding: '6px 14px', borderRadius: 999,
                background: filter === f.key ? 'var(--accent-color, #6366f1)' : 'var(--bg-secondary)',
                color: filter === f.key ? '#fff' : 'var(--text-secondary)',
                border: `1px solid ${filter === f.key ? 'var(--accent-color, #6366f1)' : 'var(--border-color)'}`,
                cursor: 'pointer', fontSize: 13, fontWeight: filter === f.key ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pull-to-refresh tip */}
      {refreshing && (
        <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 12, color: 'var(--text-tertiary)' }}>
          Refreshing…
        </div>
      )}

      {/* Run list */}
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 80px', WebkitOverflowScrolling: 'touch' }}
      >
        {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-tertiary)', fontSize: 14 }}>
            Loading runs…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-tertiary)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {filter === 'all' ? 'No recent runs' : `No ${filter} runs`}
            </div>
            <div style={{ fontSize: 13, marginTop: 6, opacity: 0.7 }}>Pull down to refresh</div>
          </div>
        )}

        {filtered.map(({ run, workflowName, workflowId }) => (
          <RunCard
            key={run.id}
            run={run}
            workflowName={workflowName}
            workflowId={workflowId}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        ))}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
